import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { formatDate } from '@/utils/helpers';
import BookingButtons from '../LiveCourse/BookingButtons';
import type { BookingResponse } from '@/types/booking';
import { BookingSource, BookingTypes } from '@/types/booking';
import { useUser } from '@clerk/nextjs';
import dayjs from 'dayjs';
import type { Course, LiveCourse } from '@/types/course';
import { extractCourseData } from '@/utils/courseUtils';
import { useSelectedLanguage } from '@/redux/selectors/languageSelector';
import { T } from '@tolgee/react';
import { useBookingService } from '@/hooks/useBookingService';
import { useHandleBooking } from '@/hooks/useHandleBooking';

interface ClosestEventCardProps {
  course: Course;
  onOpenModal: (course: LiveCourse, success?: boolean) => void;
  handleClick: (course: Course) => void;
}

const ClosestEventCard: React.FC<ClosestEventCardProps> = ({
  course,
  onOpenModal,
  handleClick: _handleClick,
}) => {
  const [isBooked, setIsBooked] = useState(false);
  const [booking, setBooking] = useState<BookingResponse | null>(null);
  const { user } = useUser();
  const locale = useSelectedLanguage();
  const { handleBooking, isBookingSuccessful, bookingId } = useHandleBooking();
  const { findBooking } = useBookingService();

  useEffect(() => {
    if (!user?.id) return;

    const checkBooking = async () => {
      try {
        const booking: BookingResponse | null = await findBooking(
          user?.id || '',
          course.id.toString(),
        );
        setIsBooked(!!booking);
        setBooking(booking);
      } catch (error) {
        console.error('Error checking booking:', error);
      }
    };

    void checkBooking();
  }, [course.id, user?.id]);

  const handleBookingClick = async () => {
    await handleBooking({
      externalId: course.attributes.expert.data.attributes.external_id,
      userId: user?.id,
      type: BookingTypes.COURSE,
      time: dayjs(course.attributes.date).toDate(),
      courseId: course.id,
      locale,
      source: BookingSource.USER,
    });
    if (isBookingSuccessful) {
      onOpenModal(extractCourseData(course), true);
    }
  };

  return (
    <div className="bg-primary rounded-2xl flex flex-col gap-4 text-white h-auto md:h-[640px] relative group duration-300 transition-all hover:transform hover:scale-105 cursor-pointer overflow-hidden">
      <div className="relative h-1/2">
        <div className="absolute top-6 left-6 px-4 py-2 text-primary bg-white opacity-75 rounded-2xl z-10 text-xs font-semibold">
          <T>live_class.next_class.tag.label</T>
        </div>
        <div className="w-full h-80 md:h-full relative">
          <Image
            src={course.attributes.cover.data?.attributes.url || ''}
            alt={course.attributes.title}
            fill
            className="object-cover rounded-2xl"
          />
        </div>
      </div>
      <div className="p-6 w-10/12">
        <h3 className="text-4xl mb-4">{course.attributes.title}</h3>
        <p className="text-base mb-4">
          {formatDate(course.attributes.date)}, {course.attributes.time_range}
        </p>
        <p className="text-white opacity-70 overflow-hidden text-ellipsis line-clamp-3 group-hover:opacity-0 duration-300 transition-opacity">
          {course.attributes.description}
        </p>
        <div className="opacity-0 group-hover:opacity-100 duration-300 transition-all translate-y-10 group-hover:-translate-y-10">
          <BookingButtons
            isBooked={isBooked}
            bookingSuccessful={isBookingSuccessful}
            bookingId={booking?.id || bookingId}
            onOpenModal={() => onOpenModal(extractCourseData(course))}
            handleBookingClick={() => {
              void handleBookingClick();
            }}
            courseId={course.id.toString()}
            className="m-0"
          />
        </div>
      </div>
    </div>
  );
};

export default ClosestEventCard;
